import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18nModule, I18nService } from '@pflegix/i18n';
import { GlobalSearchModel, IGlobalSearchModelData, IGlobalSearchModelResult } from '@pflegix/types';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject, Observable, Subject, debounceTime, filter, map, switchMap, takeUntil } from 'rxjs';
import { GlobalSearchApi } from '../../api/global-search.api';
import { GLOBAL_SEARCH_NAMESPACE } from '../../i18n/config/global-search-translations.config';
import { globalSearchTranslationsDE } from '../../i18n/de/global-search.translations.de';
import { globalSearchTranslationsEN } from '../../i18n/en/global-search.translations.en';
import { GlobalSearchEventService } from '../../services/global-search-event.service';
import { GlobalSearchHistoryService } from '../../services/global-search-history.service';
import { GlobalSearchOverlayComponent } from '../global-search-overlay/global-search-overlay.component';
import { GlobalSearchResultItemComponent } from '../global-search-result-item/global-search-result-item.component';
import { GlobalSearchResultWrapperComponent } from '../global-search-result-wrapper/global-search-result-wrapper.component';

@Component({
  selector: 'pfx-global-search',
  imports: [
    InputTextModule,
    ButtonModule,
    GlobalSearchOverlayComponent,
    GlobalSearchResultWrapperComponent,
    GlobalSearchResultItemComponent,
    FormsModule,
    StyleClassModule,
    I18nModule,
  ],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly globalSearchApi = inject(GlobalSearchApi);
  private readonly elementRef = inject(ElementRef);
  private readonly globalSearchEventService = inject(GlobalSearchEventService);
  private readonly i18nService = inject(I18nService);
  protected readonly globalSearchHistoryService = inject(GlobalSearchHistoryService);

  public models = input<GlobalSearchModel[]>();

  @Output() public selectedModel = new EventEmitter<IGlobalSearchModelData>();

  @Output() public closeSearchBar = new EventEmitter<void>();

  @ViewChild('searchInput') public searchInput!: ElementRef;

  @ViewChild('searchIcon') public searchIcon!: ElementRef;

  protected filter = '';

  protected globalSearchResult!: IGlobalSearchModelResult[];

  protected isOverlayVisible = false;

  protected isLoading = false;

  protected searchHistory: IGlobalSearchModelData[] = [];

  protected selectedIndex = -1;

  protected readonly GLOBAL_SEARCH_NAMESPACE = GLOBAL_SEARCH_NAMESPACE;

  private _filter: BehaviorSubject<string> = new BehaviorSubject('');

  private filter$ = this._filter.asObservable();

  private globalSearchRequest$: Observable<IGlobalSearchModelResult[]> = this.filter$.pipe(
    filter(GlobalSearchComponent.checkIsFilterProcessable),
    map((_filter) => _filter),
    debounceTime(500),
    switchMap((_filter) =>
      this.globalSearchApi.getCustomersByFilter(GlobalSearchComponent.sanitizeFilter(_filter), this.models())
    )
  );

  private destroy$ = new Subject<void>();

  constructor() {
    this.loadTranslations();
  }

  public ngOnInit(): void {
    this.filter$.pipe(takeUntil(this.destroy$)).subscribe((a) => {
      this.isLoading = GlobalSearchComponent.checkIsFilterProcessable(a);
    });
    this.globalSearchRequest$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.globalSearchResult = result;
      this.showOverlay();
      this.isLoading = false;
      // Select first result if available
      const items = this.getVisibleItems();

      this.selectedIndex = items.length > 0 ? 0 : -1;
    });
    this.globalSearchEventService.selectedModel$.pipe(takeUntil(this.destroy$)).subscribe((model) => {
      this.selectedModel.emit(model);
      this.globalSearchHistoryService.addToHistory(model);
      this.searchHistory = this.globalSearchHistoryService.getSearchHistory();
    });

    // Load initial search history and select first item if available
    this.searchHistory = this.globalSearchHistoryService.getSearchHistory();
    if (this.searchHistory.length > 0) {
      this.selectedIndex = 0;
    }
  }

  public ngAfterViewInit(): void {
    this.searchInput?.nativeElement.focus();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  public click(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target)) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  @HostListener('window:keydown', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.hideOverlay();
    } else if (this.isOverlayVisible) {
      this.handleKeyboardNavigation(event);
    }
  }

  private handleKeyboardNavigation(event: KeyboardEvent): void {
    const items = this.getVisibleItems();

    if (items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % items.length;
        this.scrollSelectedIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = this.selectedIndex <= 0 ? items.length - 1 : this.selectedIndex - 1;
        this.scrollSelectedIntoView();
        break;
      case 'Enter':
        if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
          event.preventDefault();
          const selectedItem = items[this.selectedIndex];

          this.selectedModel.emit(selectedItem);
          this.globalSearchHistoryService.addToHistory(selectedItem);
          this.searchHistory = this.globalSearchHistoryService.getSearchHistory();
          this.hideOverlay();
        }
        break;
      default:
        break;
    }
  }

  private scrollSelectedIntoView(): void {
    setTimeout(() => {
      const selectedElement = document.querySelector('.search-result-item.selected');

      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  private getVisibleItems(): IGlobalSearchModelData[] {
    if (this.filter) {
      return this.globalSearchResult?.flatMap((result) => result.data) ?? [];
    }
    return this.searchHistory;
  }

  protected isItemSelected(index: number): boolean {
    return this.selectedIndex === index;
  }

  protected handleFilterChanged(newFilter: string): void {
    this._filter.next(newFilter);
    // Reset selection when filter changes
    this.selectedIndex = -1;
  }

  protected get isFilterProcessable(): boolean {
    return this.filter.length >= 3;
  }

  protected deleteHistoryItem(item: IGlobalSearchModelData): void {
    this.globalSearchHistoryService.deleteFromHistory(item);
    this.searchHistory = this.globalSearchHistoryService.getSearchHistory();
  }

  protected close(): void {
    this.closeSearchBar.emit();
  }

  protected resetFilter(): void {
    this.filter = '';
    this._filter.next('');
    this.searchInput?.nativeElement.focus();
  }

  protected showOverlay(): void {
    if (this.isOverlayVisible) {
      return;
    }
    this.searchInput?.nativeElement.focus();
    this.isOverlayVisible = true;
  }

  protected hideOverlay(): void {
    if (!this.isOverlayVisible) {
      return;
    }
    this.searchIcon?.nativeElement.click();
    this.isOverlayVisible = false;
    this.resetFilter();
  }

  private loadTranslations(): void {
    this.i18nService.addTranslations({
      namespace: GLOBAL_SEARCH_NAMESPACE,
      de: globalSearchTranslationsDE,
      en: globalSearchTranslationsEN,
    });
  }

  private static checkIsFilterProcessable(filter1: string): boolean {
    return filter1.length >= 3;
  }

  private static sanitizeFilter(filter1: string): string {
    // Replace commas and semicolons with spaces, collapse multiple spaces, and trim
    return filter1.replace(/[;,]/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
